/**
 * "Cloud posture" on the DSPM dashboard (bank row II-17): the latest result of
 * each read-only check the edge scanner ran against the cloud account, with
 * the exact setting, the Discovery assets it is linked to and whether a
 * finding was raised. A check the scanner's role could not make reads "Not
 * checked" with the reason; it is never shown as compliant.
 *
 * Release 8 adds the database exposure checks to the same list: MySQL, SQL
 * Server and Oracle grants to PUBLIC, guest, anonymous or any-host accounts
 * that reach tables holding personal data, filtered by database type. A
 * database result shows each exposure with the likely owner and the statement
 * that would remove it (text only: Discovery never runs it).
 */
import { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';

import { Badge } from '@/components/ui/badge';
import { GlassCard, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { problemDetail } from '@/lib/api';
import {
  isDatabaseProvider,
  loadCloudPosture,
  POSTURE_PROVIDERS,
  PROVIDER_LABELS,
  type PostureCheck,
  type PostureList,
  type PostureProvider,
} from '@/lib/cloud-posture-api';

export const CHECK_LABELS: Record<string, string> = {
  s3_block_public_access: 'S3 Block Public Access',
  s3_acl_public: 'S3 bucket ACL',
  s3_policy_public: 'S3 bucket policy',
  s3_default_encryption: 'S3 default encryption',
  s3_tls_only: 'S3 TLS only (bucket policy)',
  s3_access_logging: 'S3 server access logging',
  s3_versioning: 'S3 versioning',
  rds_publicly_accessible: 'RDS public access',
  rds_storage_encrypted: 'RDS storage encryption',
  rds_security_group_open: 'RDS port open to the internet',
  rds_snapshot_public: 'RDS snapshot public',
  rds_snapshot_encrypted: 'RDS snapshot encryption',
  iam_wildcard_data_access: 'IAM wildcard on data stores',
  mysql_account_any_host: 'MySQL account open to any host',
  mysql_anonymous_account: 'MySQL anonymous account',
  mysql_wildcard_schema_grant: 'MySQL wildcard schema grant',
  mssql_public_permission: 'SQL Server permission to public',
  mssql_guest_access: 'SQL Server guest access',
  oracle_public_object_privilege: 'Oracle object privilege to PUBLIC',
  oracle_public_system_privilege: 'Oracle system privilege to PUBLIC',
};

const STATUS_LABEL: Record<string, string> = { violation: 'Violation', compliant: 'Compliant', not_checked: 'Not checked' };
const STATUS_CLASS: Record<string, string> = {
  violation: 'text-sev-high',
  compliant: 'text-success',
  not_checked: 'text-warning',
};
const SEVERITY_CLASS: Record<string, string> = {
  critical: 'text-sev-critical',
  high: 'text-sev-high',
  medium: 'text-sev-medium',
  low: 'text-sev-low',
};

/** A result not checked again for this long is marked stale. */
export const POSTURE_STALE_AFTER_MS = 24 * 60 * 60 * 1000;
/** A result this much older than the newest result was left out of a later
 * run (a run takes at most about 20 minutes): the resource may be gone, or
 * the check may no longer run. */
export const POSTURE_MISSED_BY_RUN_MS = 60 * 60 * 1000;
/** No posture run for this long: the whole tab is flagged. */
export const POSTURE_RUN_OVERDUE_MS = 24 * 60 * 60 * 1000;

const ms = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isFinite(t) ? t : null;
};

/** The newest result's time: the server's `lastRunAt`, else the rows' own. */
export function lastRunOf(data: Pick<PostureList, 'lastRunAt' | 'checks'>): number | null {
  const fromServer = ms(data.lastRunAt);
  if (fromServer !== null) return fromServer;
  const times = data.checks.map((c) => ms(c.lastCheckedAt)).filter((t): t is number => t !== null);
  return times.length > 0 ? Math.max(...times) : null;
}

/**
 * Why a result is stale, or null. Stale when it was not checked in the last
 * 24 hours, or when a later run did not include it. A stale "Compliant" is
 * not evidence that the setting is compliant now.
 */
export function staleReason(c: Pick<PostureCheck, 'lastCheckedAt'>, lastRun: number | null, now: number): string | null {
  const at = ms(c.lastCheckedAt);
  if (at === null) return 'when it was last checked is not known';
  if (now - at > POSTURE_STALE_AFTER_MS) return 'not checked in the last 24 hours';
  if (lastRun !== null && lastRun - at > POSTURE_MISSED_BY_RUN_MS) return 'the latest posture run did not include it';
  return null;
}

/** Hours since the last run when it is overdue, else null. */
export function overdueHours(lastRun: number | null, now: number): number | null {
  if (lastRun === null) return null;
  const age = now - lastRun;
  return age > POSTURE_RUN_OVERDUE_MS ? Math.floor(age / (60 * 60 * 1000)) : null;
}

/** A timestamp in IST, e.g. `16 Oct 2026, 21:05 IST`. */
export function istTime(iso: string): string {
  const t = ms(iso);
  if (t === null) return 'unknown';
  return `${new Intl.DateTimeFormat('en-IN', {
    timeZone: 'Asia/Kolkata',
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(new Date(t))} IST`;
}

export function CloudPostureOverdue({ lastRun, now, provider }: { lastRun: number | null; now: number; provider?: PostureProvider | undefined }) {
  const hours = overdueHours(lastRun, now);
  if (hours === null || lastRun === null) return null;
  const database = provider !== undefined && isDatabaseProvider(provider);
  return (
    <div className="rounded border border-warning/40 p-2 text-xs text-warning" role="alert" data-testid="cloud-posture-overdue">
      No {provider !== undefined && database ? `${PROVIDER_LABELS[provider]} exposure` : 'cloud posture'} run has arrived for{' '}
      {hours >= 48 ? `${Math.floor(hours / 24)} days` : `${hours} hours`} (the last
      result arrived {istTime(new Date(lastRun).toISOString())}). The results below may be out of date: a Compliant result is not
      evidence of the setting now. Check that the edge scanner is running with{' '}
      {database ? 'DB_EXPOSURE_CHECKS=true' : 'L1_AWS_POSTURE=true'} and can reach the controller.
    </div>
  );
}

/**
 * A manual snapshot that is not public but is shared with specific AWS
 * accounts: shown apart from "Compliant", so cross-account sharing of a
 * database copy is never read as nothing to see.
 */
export function sharedWithAccounts(c: Pick<PostureCheck, 'check' | 'status' | 'setting'>): number {
  if (c.check !== 'rds_snapshot_public' || c.status !== 'compliant') return 0;
  const n = c.setting && typeof c.setting === 'object' ? (c.setting as Record<string, unknown>)['sharedWithAccounts'] : undefined;
  return typeof n === 'number' && n > 0 ? n : 0;
}

/** What the posture run does not check, shown with every list. */
export const NOT_CHECKED_COVERAGE: readonly string[] = [
  'AWS-managed IAM policies and inline (user, group and role) policies: only customer-managed policies that are attached are read.',
  'Account-level S3 Block Public Access: only each bucket\'s own setting is read.',
  'RDS instances and snapshots outside the edge scanner\'s region (S3 and IAM are account-wide).',
  'CloudTrail S3 data events (only server access logging is read).',
  'Scanned databases linked to an RDS instance by its endpoint host and port only (and, for Aurora, the cluster writer and reader endpoints): a database scanned under a custom DNS name or through a proxy is not linked to its instance.',
];

/** What the database exposure checks do not look at. */
export const DATABASE_NOT_CHECKED_COVERAGE: readonly string[] = [
  'Grants to named accounts and roles: findings are raised only for PUBLIC, guest (when it holds a data privilege), the anonymous MySQL account and accounts open to any host (%). A named, host-restricted MySQL account with a wide grant, and a guest that can only connect, are listed as information, not as findings.',
  'MySQL roles (locked accounts), column privileges in mysql.columns_priv, and host patterns that are not "any host" (for example 10.%).',
  'SQL Server server-level permissions and logins; a DENY of the same permission cancels a grant to public or guest, nothing else about DENY is read.',
  'Oracle roles granted to ordinary users, and schemas Oracle maintains itself.',
  'Tables Discovery has not scanned, or scanned under another host name than the scan target (a proxy or DNS alias): the exposure is listed here and no finding is raised.',
];

export function CloudPostureCoverage({ provider }: { provider?: PostureProvider | undefined }) {
  const database = provider !== undefined && isDatabaseProvider(provider);
  const list =
    provider === undefined
      ? [...NOT_CHECKED_COVERAGE, ...DATABASE_NOT_CHECKED_COVERAGE]
      : database
        ? DATABASE_NOT_CHECKED_COVERAGE
        : NOT_CHECKED_COVERAGE;
  return (
    <details className="text-xs text-muted-foreground" data-testid="cloud-posture-coverage">
      <summary className="cursor-pointer">Not checked by this run</summary>
      <ul className="list-disc pl-5">
        {list.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </details>
  );
}

const OWNER_SOURCE_LABEL: Record<string, string> = {
  schema_owner: 'owns the schema',
  object_owner: 'owns the table',
  database_owner: 'owns the database',
  grant_option_holder: 'holds the grant option (inferred)',
  definer: 'defines objects in the schema (inferred)',
};

interface StoredExposure {
  scope?: string;
  database?: string;
  schema?: string;
  table?: string;
  columns?: string[];
  schemaPattern?: boolean;
  grantee?: string;
  privileges?: string[];
  severity?: string;
  owner?: { name?: string; source?: string };
  revoke?: string[];
}

/** Where an exposure applies, in words. */
export function exposureWhere(e: StoredExposure): string {
  switch (e.scope) {
    case 'server':
      return 'every schema on the server';
    case 'database':
      return e.database ? `every table in database ${e.database}` : 'every table in the database';
    case 'schema':
      return e.schemaPattern ? `every schema matching ${e.schema}` : `schema ${e.schema}`;
    default:
      return `${e.schema}.${e.table}${e.columns && e.columns.length > 0 ? ` (columns ${e.columns.join(', ')})` : ''}`;
  }
}

/** The exposures stored on a database result, read defensively. */
export function storedExposures(setting: unknown): { list: StoredExposure[]; total: number; omitted: number } {
  const s = setting && typeof setting === 'object' ? (setting as Record<string, unknown>) : {};
  const list = Array.isArray(s['exposures']) ? (s['exposures'] as StoredExposure[]) : [];
  const total = typeof s['exposureCount'] === 'number' ? s['exposureCount'] : list.length;
  const omitted = typeof s['exposuresOmitted'] === 'number' ? s['exposuresOmitted'] : 0;
  return { list, total, omitted };
}

/** The information lines of a compliant database result (what was seen and is not a finding). */
export function storedInfo(setting: unknown): string[] {
  const s = setting && typeof setting === 'object' ? (setting as Record<string, unknown>) : {};
  return Array.isArray(s['info']) ? (s['info'] as unknown[]).filter((x): x is string => typeof x === 'string') : [];
}

/** A viewer limited to some business units is not sent the store's exposures (they name other units' tables). */
export function exposuresHidden(setting: unknown): boolean {
  return !!setting && typeof setting === 'object' && (setting as Record<string, unknown>)['exposuresHidden'] === true;
}

/**
 * The exposures of a database result: who holds what where, the likely owner
 * and the statement that would remove it. The statement is text only.
 */
export function DatabaseExposureList({ setting }: { setting: unknown }) {
  const { list, total, omitted } = storedExposures(setting);
  if (list.length === 0) return null;
  const hidden = total - list.length + omitted;
  return (
    <ul className="space-y-1" data-testid="db-exposure-list">
      {list.map((e, i) => (
        <li key={`${e.grantee}-${e.scope}-${e.schema}-${e.table}-${i}`}>
          <span className="font-mono text-[10px] break-all">{e.grantee}</span> holds {(e.privileges ?? []).join(', ')} on {exposureWhere(e)}
          {e.severity && (
            <Badge variant="glass" className={`ml-1 text-[10px] ${SEVERITY_CLASS[e.severity] ?? ''}`}>
              {e.severity}
            </Badge>
          )}
          {e.owner?.name && (
            <div className="text-muted-foreground" data-testid="db-exposure-owner">
              Likely owner: {e.owner.name} ({OWNER_SOURCE_LABEL[e.owner.source ?? ''] ?? 'named by the database'})
            </div>
          )}
          {e.revoke && e.revoke[0] && (
            <div
              className="font-mono text-[10px] break-all text-muted-foreground"
              data-testid="db-exposure-revoke"
              title="Text only: Discovery never runs it"
            >
              {e.revoke[0]}
            </div>
          )}
        </li>
      ))}
      {hidden > 0 && (
        <li className="text-muted-foreground" data-testid="db-exposure-more">
          and {hidden} more exposure{hidden === 1 ? '' : 's'} (each table's finding lists the ones that reach it).
        </li>
      )}
      <li className="text-muted-foreground">The statements are text for your DBA: Discovery never runs them.</li>
    </ul>
  );
}

/** The setting, as `key: value` pairs, one level deep. */
export function settingText(setting: unknown): string {
  if (!setting || typeof setting !== 'object' || Array.isArray(setting)) return '';
  return Object.entries(setting as Record<string, unknown>)
    .map(([k, v]) => `${k}: ${typeof v === 'string' ? v : JSON.stringify(v)}`)
    .join('; ');
}

export function CloudPostureSummary({
  counts,
  auditPending = 0,
  scope = 'all',
  shown,
  total,
}: {
  counts: PostureList['counts'];
  auditPending?: number;
  scope?: PostureList['scope'];
  /** Rows in the list, and every result matching: "showing N of M" when cut. */
  shown?: number;
  total?: number;
}) {
  return (
    <div className="space-y-1 text-xs">
      {shown !== undefined && total !== undefined && total > shown && (
        <div className="text-warning" data-testid="cloud-posture-truncated">
          Showing {shown} of {total} results (violations first, then not checked, then compliant).
        </div>
      )}
      <div className="flex flex-wrap gap-3" data-testid="cloud-posture-counts">
        <span className={STATUS_CLASS['violation']}>{counts.violation ?? 0} violations</span>
        <span className={STATUS_CLASS['not_checked']}>{counts.not_checked ?? 0} not checked</span>
        <span className={STATUS_CLASS['compliant']}>{counts.compliant ?? 0} compliant</span>
      </div>
      {scope === 'linked' && (
        <div className="text-muted-foreground" data-testid="cloud-posture-linked-only">
          Your access is limited to some business units, so only results linked to your units' assets are shown. Account-wide results
          (IAM policies, services that could not be listed, stores Discovery does not know) are shown to people who see all units.
        </div>
      )}
      {auditPending > 0 && (
        <div className="text-warning" data-testid="cloud-posture-audit-pending">
          {auditPending} posture audit event{auditPending === 1 ? ' is' : 's are'} waiting to be written to the audit chain. The next
          posture run writes {auditPending === 1 ? 'it' : 'them'}.
        </div>
      )}
    </div>
  );
}

export function CloudPostureTable({
  checks,
  lastRun = null,
  lastRunByProvider,
  now = Date.now(),
  scope = 'all',
  provider,
}: {
  checks: PostureCheck[];
  lastRun?: number | null;
  /** Each provider is judged against its own newest run (AWS and database runs are separate). */
  lastRunByProvider?: Record<string, number | null>;
  now?: number;
  /** `linked`: a unit-limited view, which cannot tell whether any run arrived. */
  scope?: string;
  provider?: PostureProvider | undefined;
}) {
  const database = provider !== undefined && isDatabaseProvider(provider);
  if (checks.length === 0) {
    // Never a run: an empty list is not "all compliant". Say so plainly.
    return lastRun === null && scope !== 'linked' ? (
      <div className="text-xs text-sev-medium" data-testid="cloud-posture-empty">
        {provider !== undefined && database
          ? `No ${PROVIDER_LABELS[provider]} exposure run has been received yet, so nothing here has been checked. Results arrive when an edge scanner that scans a ${PROVIDER_LABELS[provider]} target runs with DB_EXPOSURE_CHECKS=true. If these checks are expected, check that setting and the scanner's run health, or contact support@foctta.com.`
          : "No cloud posture run has been received yet, so nothing here has been checked. Results arrive when an edge scanner with cloud enumeration (L1_AWS_REGION) runs with L1_AWS_POSTURE=true, or, for MySQL, SQL Server and Oracle, with DB_EXPOSURE_CHECKS=true. If posture checks are expected for this account, check that setting and the scanner's run health, or contact support@foctta.com."}
      </div>
    ) : (
      <div className="text-xs text-muted-foreground" data-testid="cloud-posture-empty">
        No cloud posture results to show for your view (a view limited to business units lists only the results linked to
        their assets).
      </div>
    );
  }
  return (
    <table className="w-full text-xs" data-testid="cloud-posture-table">
      <thead>
        <tr className="text-left text-muted-foreground">
          <th className="py-1 pr-2 font-medium">Status</th>
          <th className="py-1 pr-2 font-medium">Check</th>
          <th className="py-1 pr-2 font-medium">Resource</th>
          <th className="py-1 pr-2 font-medium">Setting</th>
          <th className="py-1 pr-2 font-medium">Last checked</th>
          <th className="py-1 font-medium">Linked assets</th>
        </tr>
      </thead>
      <tbody>
        {checks.map((c) => {
          const stale = staleReason(c, lastRunByProvider?.[c.provider] ?? lastRun, now);
          const shared = sharedWithAccounts(c);
          const more = (c.assetCount ?? c.assets.length) - c.assets.length;
          return (
          <tr key={c.id} className="border-t border-border/30 align-top" data-status={c.status} data-stale={stale ? 'true' : 'false'}>
            <td className="py-1 pr-2 whitespace-nowrap">
              {shared > 0 ? (
                <span className="text-warning" data-testid="cloud-posture-shared">
                  Not public, shared with {shared} account{shared === 1 ? '' : 's'}
                </span>
              ) : (
                <span className={STATUS_CLASS[c.status] ?? ''}>{STATUS_LABEL[c.status] ?? c.status}</span>
              )}
              {c.severity && (
                <Badge variant="glass" className={`ml-1 text-[10px] ${SEVERITY_CLASS[c.severity] ?? ''}`}>{c.severity}</Badge>
              )}
            </td>
            <td className="py-1 pr-2">
              {CHECK_LABELS[c.check] ?? c.check}
              {isDatabaseProvider(c.provider) && (
                <Badge variant="glass" className="ml-1 text-[10px]" data-testid="posture-provider-badge">
                  {PROVIDER_LABELS[c.provider as PostureProvider]}
                </Badge>
              )}
            </td>
            <td className="py-1 pr-2">
              <div>{c.resourceName}</div>
              <div className="font-mono text-[10px] text-muted-foreground break-all">{c.resourceRef}</div>
            </td>
            <td className="py-1 pr-2">
              {c.status === 'not_checked' ? (
                <span className="text-warning">{c.reason}</span>
              ) : isDatabaseProvider(c.provider) ? (
                c.status === 'violation' ? (
                  exposuresHidden(c.setting) ? (
                    <span className="text-muted-foreground" data-testid="db-exposure-hidden">
                      The exposures of this store are not listed for a view limited to some business units. The findings on your units' tables
                      name the ones that reach them.
                    </span>
                  ) : (
                    <DatabaseExposureList setting={c.setting} />
                  )
                ) : storedInfo(c.setting).length > 0 ? (
                  <div className="text-muted-foreground" data-testid="db-exposure-info">
                    No finding. For information:
                    <ul className="list-disc pl-4">
                      {storedInfo(c.setting).map((line) => (
                        <li key={line}>{line}</li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <span className="text-muted-foreground">No grant of this kind found in the latest read.</span>
                )
              ) : (
                <span className="font-mono text-[10px] break-all">{settingText(c.setting)}</span>
              )}
              {c.outcome && <div className="text-muted-foreground">{c.outcome}</div>}
            </td>
            <td className="py-1 pr-2 whitespace-nowrap" data-testid="cloud-posture-last-checked">
              <div>{istTime(c.lastCheckedAt)}</div>
              {stale && (
                <Badge variant="glass" className="text-[10px] text-warning" title={`Stale: ${stale}`} data-testid="cloud-posture-stale">
                  Stale: {stale}
                </Badge>
              )}
            </td>
            <td className="py-1">
              {c.assets.length === 0 ? (
                <span className="text-muted-foreground">None known</span>
              ) : (
                c.assets.map((a) => (
                  <div key={a.id} className="font-mono text-[10px] break-all" title={a.ref}>
                    {a.ref}
                  </div>
                ))
              )}
              {more > 0 && (
                <div className="text-muted-foreground" data-testid="cloud-posture-more-assets">
                  and {more} more linked asset{more === 1 ? '' : 's'}
                </div>
              )}
              {c.findingIds.length > 0 && (
                <div className="text-muted-foreground">
                  {c.findingIds.length} finding{c.findingIds.length === 1 ? '' : 's'} raised
                </div>
              )}
            </td>
          </tr>
          );
        })}
      </tbody>
    </table>
  );
}

/** Each provider's newest result time, from the rows in view. */
export function lastRunsByProvider(data: Pick<PostureList, 'checks'>): Record<string, number | null> {
  const out: Record<string, number | null> = {};
  for (const c of data.checks) {
    const t = ms(c.lastCheckedAt);
    if (t !== null && (out[c.provider] === undefined || (out[c.provider] ?? 0) < t)) out[c.provider] = t;
  }
  return out;
}

/** The type filter: all results, AWS, or one database engine. */
export function ProviderFilter({ value, onChange }: { value: PostureProvider | 'all'; onChange: (v: PostureProvider | 'all') => void }) {
  const options: Array<PostureProvider | 'all'> = ['all', ...POSTURE_PROVIDERS];
  return (
    <div className="flex flex-wrap gap-1 text-xs" role="group" aria-label="Filter by type" data-testid="posture-provider-filter">
      {options.map((o) => (
        <button
          key={o}
          type="button"
          aria-pressed={value === o}
          data-testid={`posture-provider-${o}`}
          className={`rounded border px-2 py-0.5 ${value === o ? 'border-primary text-primary' : 'border-border/40 text-muted-foreground'}`}
          onClick={() => onChange(o)}
        >
          {o === 'all' ? 'All' : PROVIDER_LABELS[o]}
        </button>
      ))}
    </div>
  );
}

export function CloudPosturePanel() {
  const [data, setData] = useState<PostureList | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [provider, setProvider] = useState<PostureProvider | 'all'>('all');
  useEffect(() => {
    let live = true;
    setData(null);
    setError(null);
    loadCloudPosture(provider === 'all' ? undefined : provider)
      .then((d) => live && setData(d))
      .catch((e: unknown) => live && setError(problemDetail(e, 'Cloud posture results could not be loaded.')));
    return () => {
      live = false;
    };
  }, [provider]);
  const only = provider === 'all' ? undefined : provider;
  return (
    <GlassCard data-testid="cloud-posture-panel">
      <CardHeader>
        <CardTitle>Cloud posture and database exposure</CardTitle>
        <CardDescription>
          AWS: S3 buckets (public access, default encryption, TLS only, access logging, versioning), RDS instances (public access,
          storage encryption, database port open to the internet), manual RDS snapshots (public, encrypted) and customer-managed
          IAM policies granting wildcard access to data stores. Read-only checks run by the edge scanner in your AWS account (S3
          and IAM account-wide, RDS in the scanner's region). Databases: MySQL, SQL Server and Oracle grants to PUBLIC, guest,
          anonymous or any-host accounts, read from each database's own grant catalogue with the scanner's existing credentials,
          read-only. A finding is raised only on a store Discovery knows holds classified personal data (for RDS: a table Discovery
          scanned through the instance's endpoint; for a database: the table the grant reaches); every other result is listed here.
        </CardDescription>
      </CardHeader>
      <div className="px-6 pb-6 space-y-3">
        <ProviderFilter value={provider} onChange={setProvider} />
        {error ? (
          <div className="text-xs text-sev-high">{error}</div>
        ) : data ? (
          <>
            <CloudPostureOverdue lastRun={lastRunOf(data)} now={ms(data.serverTime) ?? Date.now()} provider={only} />
            <CloudPostureSummary
              counts={data.counts}
              auditPending={data.auditPending ?? 0}
              scope={data.scope ?? 'all'}
              shown={data.checks.length}
              total={data.total}
            />
            <CloudPostureCoverage provider={only} />
            <CloudPostureTable
              checks={data.checks}
              lastRun={lastRunOf(data)}
              lastRunByProvider={lastRunsByProvider(data)}
              now={ms(data.serverTime) ?? Date.now()}
              scope={data.scope ?? 'all'}
              provider={only}
            />
          </>
        ) : (
          <Loader2 className="h-4 w-4 animate-spin" aria-label="Loading" />
        )}
      </div>
    </GlassCard>
  );
}
